/* The one shape a workspace uses to say what belongs in the header, and the seam it says
 * it through.
 *
 * The console has a single header, owned by the shell layout, but what fills it is
 * per-workspace knowledge that lives one route level *below* the shell — so a workspace
 * layout registers its header contribution on mount and the shell reads it back. This is
 * the whole composition point; there is no other way to put something in the header.
 *
 * Six things arrive through it, because six things in the header are the workspace's to
 * know: its `items` (the nav), an optional compact `nested` route group, its route
 * `actions`, its `status`
 * (Fleet's live-stream badge, Label's connectivity indicator), its `tools` (device controls
 * a workspace needs at hand — Label's full-screen button, whose tooltip is translated,
 * which is why the node comes from the workspace and not the shell), and its account
 * `badges` (what THIS door grants this identity: the factory role, the catalog's scoped
 * grants, the labeler seat's role). One registration rather than five seams: it is one fact
 * — "here is my header" — and keying it by area is what stops a workspace left behind by a
 * navigation from painting the header of the one that replaced it.
 *
 * "Area", not "workspace", because Settings registers through the same seam: it has a nav
 * and it fills the same header, it just isn't a pillar destination. Generalizing the key
 * was the whole cost of giving it a home.
 *
 * The item shape is frozen here on purpose: it is the contract every workspace lift must
 * fit into, and it was refined against their existing NAV arrays.
 *
 *   - `label` is the resolved human text, never a key. Only the Label workspace is
 *     translated (the floor works in Hindi), and it resolves its labels at registration
 *     time against the persisted language — so the shell renders one thing verbatim and
 *     never carries a translation table it cannot read.
 *   - `end` mirrors react-router's NavLink flag the five NavLink users already pass: index
 *     items match exactly, section items match by prefix.
 *   - There is deliberately NO `visible` predicate. Both role gates in the tree today
 *     (Label's `role === "manager" | "customer"`, Fleet's `org_kind !== "teleop_vendor"`)
 *     read a typed `me` that only the workspace layout holds — it is the thing that fetches
 *     it. A predicate here would have to take an untyped user and hand every workspace a
 *     cast; instead the workspace filters its own list with its own types and registers the
 *     result. Gating is then a re-registration, which is also what a role change should do.
 */

import type { IconName } from "@sx/ui";
import type { ReactNode } from "react";
import { useCallback, useSyncExternalStore } from "react";
import type { LinkProps } from "@tanstack/react-router";
import type { BadgeProps } from "@sx/ui";

import {
  CONSOLE_WORKSPACES,
  NAVIGABLE_ROUTES,
  PROJECT_SCOPE_PREFIX,
  type ConsoleWorkspace,
  type WorkspaceId,
} from "../platform.gen";

export type { WorkspaceId };

/** Whether a person may open this address.
 *
 * The answer is the quality ledger's, generated into `NAVIGABLE_ROUTES`: an address
 * whose row reads `works` or `degraded` is open, and every other registered address is
 * closed and shows the typed not-found (`shell/NotFound.tsx`). Two readers and no
 * third — the outlet, which decides what an address renders, and the shell, which
 * decides what navigation offers. Fails closed, so an address the set does not name is
 * shut rather than assumed.
 *
 * Takes a route *template* — `/p/$org/$project/library`, the spelling both the route
 * tree and a nav item's `to` use — and not a filled-in pathname, because the ledger
 * answers for the address and not for one visit to it.
 */
export function isNavigable(routeTemplate: string): boolean {
  const address =
    routeTemplate.length > 1 && routeTemplate.endsWith("/")
      ? routeTemplate.slice(0, -1)
      : routeTemplate;
  return NAVIGABLE_ROUTES.has(address);
}

/** The project as an address: the two slugs that name it, and nothing else.
 *
 * `platform/auth/migrations/0005_projects.sql` makes a project's slug unique inside its
 * organization, so the pair is a total coordinate — which is why the console spells the
 * project here and nowhere else. */
export interface ProjectAddress {
  readonly org: string;
  readonly project: string;
}

/** A workspace with its destination resolved: where *this* visit goes.
 *
 * A `routeBase` is a route template now (`/p/$org/$project/library`), so it is not an
 * address until a project fills it in. Only the shell knows which project that is, so it
 * resolves the row once and everything that offers the walk — the launcher, the command
 * palette — reads the same `href`. Interpolating the template twice is how one of them
 * ends up navigating to a literal `$org`. */
export type WorkspaceDestination = Omit<ConsoleWorkspace, "name"> & {
  /** Contextual presentation; the generated workspace identity stays unchanged. */
  readonly name: string;
  readonly href: string;
  /** A navigation hint, never permission to read or act in the workspace. */
  readonly accessRequired?: boolean;
};

/** The pathname a project-scoped workspace has for one project. */
export function projectScopedPath(
  address: ProjectAddress,
  workspace: ConsoleWorkspace,
): string {
  const suffix = workspace.routeBase.slice(PROJECT_SCOPE_PREFIX.length);
  return `/p/${encodeURIComponent(address.org)}/${encodeURIComponent(
    address.project,
  )}${suffix}`;
}

/** An addressable area of the console: one of the workspaces or Settings.
 *
 * Settings is not a workspace — it is account context, reached from the header
 * rather than the launcher, and it belongs to no pillar. But it is an area with its own
 * nav, so everything below the shell that is keyed "per area" is keyed by this. */
export type ConsoleArea = WorkspaceId | "settings";

export const SETTINGS_ROUTE_BASE = "/settings";
/** The unscoped front door: where a project is chosen or created, before there is one. */
export const PROJECT_ROUTE_BASE = "/projects";

export interface WorkspaceNavItem {
  /** Absolute console route, e.g. `/label/admin` — the router's typed address space,
   * so a typo'd nav destination is a compile error, not a dead header link. */
  readonly to: NonNullable<LinkProps["to"]>;
  /** Typed path parameters for a dynamic route such as one Project's workspace. */
  readonly params?: LinkProps["params"];
  /** Human text, already resolved in the operator's language. */
  readonly label: string;
  readonly icon: IconName;
  /** This item is its group's INDEX: it owns its own address and every address below it
   *  that no sibling item owns — see `activeNavItem`. */
  readonly end?: boolean;
  /** Additional routes owned by this view, without changing its entry link. */
  readonly activeOn?: readonly NonNullable<LinkProps["to"]>[];
  /** Addresses this view owns themselves, and nothing below them. */
  readonly activeAt?: readonly NonNullable<LinkProps["to"]>[];
  /** The view owns its own address and nothing below it: the project's home is Operation,
   *  while the Library under it is not. */
  readonly exact?: boolean;
}

/** Which item in a nav group the current address is on.
 *
 * One rule, and the same one `matchConsoleArea` uses: the longest owned path wins. A plain
 * item owns everything below it. An index item (`end`) owns its own address and anything
 * below it that no SIBLING owns — which is what "exact" was reaching for and got wrong the
 * moment an identity became a path segment: Deployments' Robots is still lit on one robot at
 * `/p/<org>/<project>/deploy/robot-7`, and still not on its Monitor at `…/deploy/stations`.
 *
 * Returns the item's index, or `undefined` when the address is on none of them.
 */
export function activeNavItem(
  paths: readonly string[],
  pathname: string,
  ends: readonly (boolean | undefined)[] = [],
): number | undefined {
  const owns = (path: string): boolean =>
    pathname === path || pathname.startsWith(`${path}/`);
  let best: number | undefined;
  for (const [index, path] of paths.entries()) {
    if (!owns(path)) continue;
    if (
      ends[index] === true &&
      pathname !== path &&
      paths.some((other, otherIndex) => otherIndex !== index && owns(other))
    ) {
      continue;
    }
    if (best === undefined || paths[best].length < path.length) best = index;
  }
  return best;
}

/** A compact set of routes nested below one workspace destination.
 *
 * The shell renders this beneath the active workspace in the desktop rail and folds the same
 * items into the existing navigator on smaller screens. This keeps a deep tool
 * addressable without giving it a second header or a route-local sidebar. */
export interface WorkspaceNavGroup {
  readonly label: string;
  readonly items: ReadonlyArray<WorkspaceNavItem>;
}

/** One badge in the account menu: what the *workspace's own door* grants this identity —
 * the factory role, a catalog `role · scope` grant, the labeler seat's role. The platform
 * identity itself (name, subject, org) is the shell's, and is never re-stated here. */
export interface AccountBadge {
  readonly label: string;
  readonly variant?: BadgeProps["variant"];
}

export interface WorkspaceHeader {
  readonly workspace: ConsoleArea;
  readonly items: ReadonlyArray<WorkspaceNavItem>;
  readonly presentation?: "tabs";
  /** Routes nested below the active workspace page, kept inside the shared navigation. */
  readonly nested?: WorkspaceNavGroup;
  /** Important route actions that should remain available without becoming peer pages. */
  readonly actions?: ReadonlyArray<WorkspaceNavItem>;
  /** The scope the header names once, non-interactively: the door-owned site, queue or
   * ticket a producer surface works in (so an organization-wide view never looks
   * accidentally project-scoped), or the selected Project on a project-scoped area, whose
   * tabs all belong to it. Selecting stays in the account menu; this only says which. */
  readonly scope?: string;
  /** Rendered right of the nav (Fleet's live badge, Label's connectivity indicator). */
  readonly status?: ReactNode;
  /** Device controls this workspace needs at hand, rendered beside the account menu. */
  readonly tools?: ReactNode;
  /** This door's grants, shown inside the account menu while the workspace is active. */
  readonly badges?: ReadonlyArray<AccountBadge>;
}

const registered = new Map<ConsoleArea, WorkspaceHeader>();
const listeners = new Set<() => void>();

function publish(): void {
  for (const listener of listeners) listener();
}

/** Publish a workspace's header contribution; the returned function withdraws it.
 *
 * Keyed by workspace, so a workspace left behind by a navigation cannot paint the header of
 * the one that replaced it — the shell only ever asks for the active workspace. */
export function registerWorkspaceHeader(header: WorkspaceHeader): () => void {
  registered.set(header.workspace, header);
  publish();
  return () => {
    if (registered.get(header.workspace) === header)
      registered.delete(header.workspace);
    publish();
  };
}

/** The active workspace's header — undefined until it registers one. */
export function useWorkspaceHeader(
  workspace: ConsoleArea,
): WorkspaceHeader | undefined {
  const subscribe = useCallback((onChange: () => void) => {
    listeners.add(onChange);
    return () => listeners.delete(onChange);
  }, []);
  const read = useCallback(() => registered.get(workspace), [workspace]);
  return useSyncExternalStore(subscribe, read, read);
}

/** Whether a pathname sits under a route template, comparing segment by segment.
 *
 * A workspace's `routeBase` is now a route *template* — `/p/$org/$project/library` — because
 * the project is a coordinate in the address rather than a query beside it. A parameter
 * segment matches whatever is in that position; a literal segment must be exactly itself, so
 * `/library-of-congress` is not the Library. */
function underRouteBase(pathname: string, routeBase: string): boolean {
  const actual = pathname.split("/").filter(Boolean);
  const template = routeBase.split("/").filter(Boolean);
  if (actual.length < template.length) return false;
  return template.every(
    (segment, index) => segment.startsWith("$") || segment === actual[index],
  );
}

/** Parse a console pathname into the area that owns it.
 *
 * Home is the intent-led entrance to Projects. Other arbitrary URLs remain genuinely
 * partial and resolve to the not-found view rather than being guessed into a workspace.
 *
 * The longest matching template wins, so a workspace nested inside the project's own
 * address (`/p/a/b/library`) is the Library rather than the Project it hangs from. That
 * replaces the old hand-ordered table, where `/fleet/deployments` had to be declared before
 * `/fleet` or supervision would swallow it.
 */
export function matchConsoleArea(pathname: string): ConsoleArea | undefined {
  if (pathname === "/") return "projects";
  if (
    pathname === SETTINGS_ROUTE_BASE ||
    pathname.startsWith(`${SETTINGS_ROUTE_BASE}/`)
  ) {
    return "settings";
  }
  if (
    pathname === PROJECT_ROUTE_BASE ||
    pathname.startsWith(`${PROJECT_ROUTE_BASE}/`)
  ) {
    return "projects";
  }
  return [...CONSOLE_WORKSPACES]
    .sort((a, b) => b.routeBase.length - a.routeBase.length)
    .find((workspace) => underRouteBase(pathname, workspace.routeBase))?.id;
}
